import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Box, Chip, IconButton, Tooltip, TextField } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, COLORS, Icon, ICONS, Modal } from "abs-ui-components";
import { useSelector } from "react-redux";
import FormHelperText from "@mui/material/FormHelperText";

import { callApi } from "../../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { useParams } from "react-router";
import axios from "axios";
import { useFormContext } from "react-hook-form";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const FeuC14DeterminationOfDensity = forwardRef(({ c26MethodForDensity, refetchMonitoringData, dropdownData, shouldRefetch,vesselMstId }, ref) => {
	const RowActions = {
		Edit: 0,
		Add: 1,
	};
	const { reset } = useFormContext();
	const pickValue = (obj, path) => {
		if (!obj) return undefined;
		if (obj[path] !== undefined) return obj[path];
		return path.split(".").reduce((acc, key) => (acc !== undefined ? acc?.[key] : undefined), obj);
	};

	const [activeAction, setactiveAction] = useState(-1);
	const [validationErrors, setValidationErrors] = useState({});
	const [fuelData, setFuelData] = useState([]);
	const [, setUpdate] = useState(0); // trigger force update
	const [measuringEquipmentOptions, setMeasuringEquipmentOptions] = useState([]);
	const fuelTypeOptions = useSelector((state) => state.myFleet.mrvDropdowns?.allfueltypes || []);
	const [refresh, setRefresh] = useState(0);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [deletingRowIndex, setDeletingRowIndex] = useState();

	const normEquip = (raw) => (raw && typeof raw === "object" ? raw.value ?? raw.measuringEquipmentId ?? "" : raw ?? "");
	useEffect(() => {
		if (Array.isArray(c26MethodForDensity)) {
			const transformedData = c26MethodForDensity?.map((item) => {
				const technicalDesc = item?.tankFuelTypes || {};
				return {
					tankFuelTypes: {
						identification: technicalDesc?.identification || "",
						otherTechnicalDescription: technicalDesc?.otherTechnicalDescription || "",
						possibleFuelTypes: technicalDesc?.possibleFuelTypes || [],
						monitoringMethod: technicalDesc?.monitoringMethod || [],
						measuringEquipment: normEquip(technicalDesc?.measuringEquipment) || "",
						levelOfUncertainity: technicalDesc?.levelOfUncertainity || "",
						uncertainityValue: technicalDesc?.uncertainityValue ?? null,
					},
					bunkeredFuelDensity: item.bunkeredFuelDensity || "",
					fuelInTanksDensity: item.fuelInTanksDensity || "",
				};
			});

			setFuelData(transformedData);
		}
	}, [c26MethodForDensity]);

	useEffect(() => {
		if (c26MethodForDensity) {
			reset((prev) => ({ ...prev, c12: c26MethodForDensity }));
		}
	}, [c26MethodForDensity, reset]);

	const fetchMonitoringDropdownData = async () => {
		try {
			const response = await callApi({
				method: "GET",
				url: API_ROUTES.GET_MEASURING_EQUIPMENTS_NAMES(vesselMstId),
			});
			const data = response;
			const options = (Array.isArray(data) ? data : []).map((item) => ({
				label: item,
				value: item,
			}));
			setMeasuringEquipmentOptions(options);
		} catch (err) {
			console.error("Error fetching measuring equipment list", err);
		}
	};

	useEffect(() => {
		fetchMonitoringDropdownData();
	}, [vesselMstId]);

	const labelByEquipmentId = useMemo(() => Object.fromEntries(measuringEquipmentOptions.map((o) => [o.value, o.label])), [measuringEquipmentOptions]);

	const handleFuelActions = async (table, row, action) => {
		if (activeAction === -1) {
			if (action === RowActions.Edit) {
				table.setEditingRow(row);
				setactiveAction(action);
			} else if (action === RowActions.Add) {
				table.setCreatingRow(true);
				setactiveAction(action);
			}
			return;
		} else return;
	};

	const resetActiveRow = () => {
		setactiveAction(-1);
		setValidationErrors({});
	};

	const validateRequired = (v) => {
		if (Array.isArray(v)) return v.length > 0;
		return v !== undefined && v !== null && String(v).trim() !== "";
	};

	const buildRowFromValues = (vals) => {
		const rawValue = pickValue(vals, "tankFuelTypes.uncertainityValue");
		let parsedValue = Number(rawValue);
		if (isNaN(parsedValue) || rawValue === "" || rawValue === null || rawValue === undefined) {
			parsedValue = 0;
		}
		return {
			tankFuelTypes: {
				identification: pickValue(vals, "tankFuelTypes.identification") || "",
				otherTechnicalDescription: pickValue(vals, "tankFuelTypes.otherTechnicalDescription") || "",
				possibleFuelTypes: pickValue(vals, "tankFuelTypes.possibleFuelTypes") || [],
				monitoringMethod: pickValue(vals, "tankFuelTypes.monitoringMethod") || [],
				measuringEquipment: normEquip(pickValue(vals, "tankFuelTypes.measuringEquipment")) || "",
				levelOfUncertainity: pickValue(vals, "tankFuelTypes.levelOfUncertainity") || "",
				uncertainityValue: parsedValue,
			},
			fuelInTanksDensity: vals.fuelInTanksDensity || "",
		};
	};

	const pushRows = async (rows, successMsg) => {
		try {
			const res = await callApi({ method: "POST", url: API_ROUTES.C14(vesselMstId), data: rows });

			if (res?.isSuccess) {
				emitToasterMsg("success", "Table C.1.4", successMsg);
				if (typeof refetchMonitoringData === "function") await refetchMonitoringData();
				return true;
			}
			emitToasterMsg("error", "Table C.1.4", res?.errorMessage);
		} catch {
			emitToasterMsg("error", "Table C.1.4", "Save failed");
		}
		return false;
	};

	const [hasFetched, setHasFetched] = useState(false);

	useEffect(() => {
		if (shouldRefetch && !hasFetched) {
			fetchMonitoringDropdownData();
			setHasFetched(true);
		}
	}, [shouldRefetch]);

	useEffect(() => {
		if (!shouldRefetch) {
			setHasFetched(false);
		}
	}, [shouldRefetch]);

	const validateUser = (row) => {
		const identification = pickValue(row, "tankFuelTypes.identification");
		const possibleFuelTypes = pickValue(row, "tankFuelTypes.possibleFuelTypes");
		const levelOfUncertainity = pickValue(row, "tankFuelTypes.levelOfUncertainity");
		return {
			identification: !validateRequired(identification) ? "Field is required" : "",
			possibleFuelTypes: !validateRequired(possibleFuelTypes) ? "Field is required" : "",
			levelOfUncertainity: !validateRequired(levelOfUncertainity) ? "Field is required" : "",
		};
	};

	useImperativeHandle(ref, () => ({
		async submitAndCheck() {
			const { creatingRow, editingRow } = table.getState();

			if (!creatingRow && !editingRow) return true;
			const mode = creatingRow ? "add" : "edit";
			const editedValues = creatingRow ? creatingRow?._valuesCache : editingRow?._valuesCache;
			let isDirty = false;
			if (editingRow && editedValues) {
				isDirty = Object.keys(editedValues).some((key) => editedValues[key] !== editingRow.original[key]);
			}
			const validationRes = validateUser(editedValues, mode, {});
			const hasErrors = Object.values(validationRes).some((err) => err);

			if (hasErrors) {
				setValidationErrors(validationRes);
				emitToasterMsg("warning", "Validation Error", "Please correct the highlighted fields before proceeding.");
				return false;
			}
			if (creatingRow) {
				await handleCreateFuel({ values: editedValues, table }, true);
				return true;
			} else if (editingRow && isDirty) {
				await handleUpdateEmissions({ values: editedValues, table, row: editingRow }, true);
				return true;
			}
			if (!isDirty) {
				setactiveAction(-1);
				table.setEditingRow(null);
				return true;
			}
			return true;
		},
	}));

	const handleCreateFuel = async ({ values, table }, isAuto = false) => {
		const errs = validateUser(values);
		if (Object.values(errs).some(Boolean)) {
			setValidationErrors(errs);
			return;
		}
		setValidationErrors({});

		const newRow = buildRowFromValues(values);
		const payload = [...fuelData.map((r) => ({ ...r })), newRow];

		if (await pushRows(payload, isAuto ? "Emission source has been autosaved successfully" : "Emission source has been added successfully")) {
			setFuelData(payload);
			table.setCreatingRow(null);
			setactiveAction(-1);
		}
	};

	const handleUpdateEmissions = async ({ values, table, row }, isAuto = false) => {
		const errs = validateUser(values);
		if (Object.values(errs).some(Boolean)) {
			setValidationErrors(errs);
			return;
		}
		setValidationErrors({});

		const updated = fuelData.map((r, idx) => (idx === row.index ? buildRowFromValues(values) : r));
		if (await pushRows(updated, isAuto ? "Emission source has been autosaved successfully" : "Emission source has been updated successfully")) {
			setFuelData(updated);
			table.setEditingRow(null);
			setactiveAction(-1);
		}
	};

	const handleDeleteEmissions = async (rowIndex) => {
		const updated = fuelData.filter((_, idx) => idx !== rowIndex);
		if (await pushRows(updated, "Emission source has been deleted successfully")) {
			setFuelData(updated);
		}
		setConfirmDelete(false);
	};

	const getPossibleFuelType = () => {
		return dropdownData?.fuelTypesOptionsInTank?.map((item) => ({
			label: item.value,
			value: item.code,
		}));
	};

	const monitoringMethodOptions = useMemo(
		() =>
			dropdownData?.monitoringMethodOptions?.map((m) => ({
				label: m.name,
				value: m.code,
			})) ?? [],
		[dropdownData]
	);

	const labelByMethod = useMemo(() => Object.fromEntries(monitoringMethodOptions.map((opt) => [opt.value, opt.label])), [monitoringMethodOptions]);

	const fuelBunkeredDensities = useMemo(
		() =>
			dropdownData?.fuelBunkeredDensities?.map((u) => ({
				label: u.value,
				value: u.code,
			})) ?? [],
		[dropdownData]
	);

	const labelByfuelBunkered = useMemo(() => Object.fromEntries(fuelBunkeredDensities.map((o) => [o.value, o.label])), [fuelBunkeredDensities]);

	const fuelTanksDensities = useMemo(
		() =>
			dropdownData?.fuelTanksDensities?.map((u) => ({
				label: u.value,
				value: u.value,
			})) ?? [],
		[dropdownData]
	);

	const labelByfuelTanker = useMemo(() => Object.fromEntries(fuelTanksDensities.map((o) => [o.value, o.label])), [fuelTanksDensities]);

	const levelOfUncertaintyOptions = useMemo(
		() =>
			dropdownData?.levelOfUncertainities?.map((u) => ({
				label: u.value,
				value: u.value,
			})) ?? [],
		[dropdownData]
	);

	const labelByUncertainty = useMemo(() => Object.fromEntries(levelOfUncertaintyOptions.map((o) => [o.value, o.label])), [levelOfUncertaintyOptions]);

	const columns = useMemo(
		() => [
			{
				id: "tankFuelTypes",
				header: "Fuel type/tank",
				muiTableHeadCellProps: {
					className: "grouped-header",
				},
				columns: [
					{
						accessorKey: "tankFuelTypes.identification",
						header: "Identification",
						muiEditTextFieldProps: ({ row }) => ({
							required: true,
							error: !!validationErrors?.identification,
							helperText: validationErrors?.identification,
							inputProps: {
								maxLength: 255,
							},
							onFocus: () =>
								setValidationErrors((prev) => ({
									...prev,
									identification: undefined,
								})),
						}),
					},

					{
						accessorKey: "tankFuelTypes.otherTechnicalDescription",
						header: "Other technical description of tank",
						muiEditTextFieldProps: {
							inputProps: {
								maxLength: 255,
							},
						},
					},
					{
						accessorKey: "tankFuelTypes.possibleFuelTypes",
						header: "Possible fuel types in tank",
						editVariant: "select",
						size: 280,
						editSelectOptions: getPossibleFuelType,
						muiEditTextFieldProps: ({ cell, row }) => ({
							select: true,
							required: true,
							value: row._valuesCache["tankFuelTypes.possibleFuelTypes"] ?? cell.getValue() ?? [],

							error: !!validationErrors?.possibleFuelTypes,
							helperText: validationErrors?.possibleFuelTypes,
							SelectProps: {
								multiple: true,

								renderValue: (selected) => {
									if (!selected?.length) return "Select fuel type(s)";

									return (
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
											{selected.map((code) => {
												const label = fuelTypeOptions.find((o) => o.code === code)?.name || code;
												return (
													<Chip
														key={code}
														label={label}
														onMouseDown={(e) => e.stopPropagation()}
														onDelete={(e) => {
															e.stopPropagation();
															const newVal = selected.filter((v) => v !== code);
															row._valuesCache["tankFuelTypes.possibleFuelTypes"] = newVal;
															row._valuesCache["possibleFuelTypes"] = newVal;
															setRefresh((r) => r + 1);
															setValidationErrors((prev) => ({
																...prev,
																possibleFuelTypes: undefined,
															}));
														}}
													/>
												);
											})}
										</Box>
									);
								},

								MenuProps: {
									disableScrollLock: true,
									textAlign: "center",
									PaperProps: {
										sx: {
											maxHeight: 250,
											maxWidth: 180,
											boxShadow: 3,
											overflowY: "auto",
											"&::-webkit-scrollbar": { width: "8px" },
											"&::-webkit-scrollbar-thumb": {
												backgroundColor: "darkgray",
												borderRadius: "3px",
											},
											"&::-webkit-scrollbar-track": { backgroundColor: "#f0f0f0" },
										},
									},
									anchorOrigin: { vertical: "bottom", horizontal: "left" },
									transformOrigin: { vertical: "top", horizontal: "left" },
									getContentAnchorEl: null,
								},
							},
							onChange: (e) => {
								const val = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;

								row._valuesCache["tankFuelTypes.possibleFuelTypes"] = val;
								row._valuesCache["possibleFuelTypes"] = val;

								setRefresh((r) => r + 1);
								setValidationErrors((prev) => ({
									...prev,
									possibleFuelTypes: undefined,
								}));
							},
						}),
						Cell: ({ cell }) => (cell.getValue() || []).map((code) => fuelTypeOptions.find((opt) => opt.code === code)?.name || code).join(", "),

						muiTableHeadCellProps: {
							sx: {
								borderBottom: "none",
								borderRight: "1px solid lightgray",
								verticalAlign: "middle",
							},
						},
					},
					{
						accessorKey: "tankFuelTypes.monitoringMethod",
						header: "Monitoring Method",
						editVariant: "select",
						size: 280,
						editSelectOptions: monitoringMethodOptions,
						muiEditTextFieldProps: ({ cell, row }) => {
							const value = row._valuesCache["tankFuelTypes.monitoringMethod"] ?? cell.getValue() ?? [];
							const handleChange = (event) => {
								const newValue = typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value;

								if (newValue.length > 1) {
									row._valuesCache["tankFuelTypes.levelOfUncertainity"] = "Ship specific estimate";
									row._valuesCache["tankFuelTypes.uncertainityValue"] = "";
								} else if (newValue.length === 1) {
									row._valuesCache["tankFuelTypes.levelOfUncertainity"] = "";
									row._valuesCache["tankFuelTypes.uncertainityValue"] = "";
								}


								row._valuesCache["tankFuelTypes.monitoringMethod"] = newValue;
								row._valuesCache["monitoringMethod"] = newValue; // Optional: fallback reference
								setRefresh((r) => r + 1);
								setValidationErrors((prev) => ({
									...prev,
									monitoringMethod: undefined,
								}));
							};

							const handleDelete = (deletedValue) => {
								const newValue = value.filter((v) => v !== deletedValue);
								row._valuesCache["tankFuelTypes.monitoringMethod"] = newValue;
								row._valuesCache["monitoringMethod"] = newValue;

								if (newValue.length > 1) {
									row._valuesCache["tankFuelTypes.levelOfUncertainity"] = "Ship specific estimate";
									row._valuesCache["tankFuelTypes.uncertainityValue"] = "";
								} else if (newValue.length === 1) {
									row._valuesCache["tankFuelTypes.levelOfUncertainity"] = "";
									row._valuesCache["tankFuelTypes.uncertainityValue"] = "";
								}
								setRefresh((r) => r + 1);
								setValidationErrors((prev) => ({
									...prev,
									monitoringMethod: undefined,
								}));
							};

							return {
								select: true,
								value,
								onChange: handleChange,
								SelectProps: {
									multiple: true,
									renderValue: (selected) => {
										if (!selected?.length) return "Select monitoring method(s)";
										return (
											<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
												{selected.map((code) => (
													<Chip
														key={code}
														label={labelByMethod[code] ?? code}
														onDelete={(e) => {
															e.stopPropagation();
															handleDelete(code);
														}}
														onMouseDown={(e) => e.stopPropagation()}
													/>
												))}
											</Box>
										);
									},
								},
							};
						},
						Cell: ({ cell }) => (cell.getValue() || []).map((code) => labelByMethod[code] ?? code).join(", "),
						muiTableHeadCellProps: {
							sx: {
								borderBottom: "none",
								borderRight: "1px solid lightgray",
								verticalAlign: "middle",
							},
						},
					},
					{
						accessorKey: "tankFuelTypes.measuringEquipment",
						header: "Measuring equipment",
						editVariant: "select", // ✅ MRT handles select rendering
						editSelectOptions: measuringEquipmentOptions.map((opt) => ({
							value: opt.value,
							text: opt.label,
						})),
						Cell: ({ cell }) => measuringEquipmentOptions.find((opt) => opt.value === cell.getValue())?.label || "",
						muiEditTextFieldProps: {
							variant: "standard", // ✅ makes it standard variant
						},
					},
					{
						accessorKey: "tankFuelTypes.levelOfUncertainity",
						header: "Level of uncertainity associated with fuel monitoring",
						editVariant: "select",
						editSelectOptions: levelOfUncertaintyOptions,
						Cell: ({ cell }) => labelByUncertainty[cell.getValue()] ?? cell.getValue(),
						muiEditTextFieldProps: ({ row }) => {
							const monitoringMethods = row._valuesCache?.["tankFuelTypes.monitoringMethod"] || [];
							const multipleMonitoringMethods = monitoringMethods.length > 1;

							let value = row._valuesCache?.["tankFuelTypes.levelOfUncertainity"] ?? row.original?.tankFuelTypes?.levelOfUncertainity ?? "";
							// if (multipleMonitoringMethods) {
							//     value = "Ship specific estimate";
							//     row._valuesCache["tankFuelTypes.levelOfUncertainity"] = value;
							//     row._valuesCache["tankFuelTypes.uncertainityValue"] = "";
							// } else if (value === "Ship specific estimate") {
							//     value = "";
							//     row._valuesCache["tankFuelTypes.levelOfUncertainity"] = "";
							//     row._valuesCache["tankFuelTypes.uncertainityValue"] = "";
							// }
							// if (multipleMonitoringMethods) {
							//     row._valuesCache["tankFuelTypes.levelOfUncertainity"] = "Ship specific estimate";
							// }

							return {
								select: true,
								required: !multipleMonitoringMethods,
								value,
								error: !multipleMonitoringMethods && !!validationErrors?.levelOfUncertainity,
								helperText: !multipleMonitoringMethods ? validationErrors?.levelOfUncertainity : "",
								disabled: multipleMonitoringMethods,
								onChange: (e) => {
									row._valuesCache["tankFuelTypes.levelOfUncertainity"] = e.target.value;

									if (e.target.value === "Default value") {
										row._valuesCache["tankFuelTypes.uncertainityValue"] = 7.5;
									} else if (e.target.value === "Ship specific estimate") {
										row._valuesCache["tankFuelTypes.uncertainityValue"] = "";
									} else {
										row._valuesCache["tankFuelTypes.uncertainityValue"] = "";
									}

									setValidationErrors({});
								},
							};
						},
					},
					{
						accessorKey: "tankFuelTypes.uncertainityValue",
						header: "Value (%)",
						muiEditTextFieldProps: ({ row }) => {
							const levelOfUncertainity = row._valuesCache?.["tankFuelTypes.levelOfUncertainity"];
							const monitoringMethods = row._valuesCache?.["tankFuelTypes.monitoringMethod"] || [];

							const multipleMonitoringMethods = monitoringMethods.length > 1;
							const isDefault = levelOfUncertainity === "Default value";

							const disabled = (!levelOfUncertainity || isDefault) && !multipleMonitoringMethods;
							let value = row._valuesCache?.["tankFuelTypes.uncertainityValue"];
							if (value === 0) value = "";

							if (isDefault) value = "7.5";

							return {
								type: "number",
								inputProps: {
									step: "0.01",
									min: "0",
								},
								value,
								required: !disabled,
								disabled,
								onChange: (e) => {
									let val = e.target.value;
									if (val === "" || isNaN(val)) {
										row._valuesCache["tankFuelTypes.uncertainityValue"] = 0;
									} else {
										row._valuesCache["tankFuelTypes.uncertainityValue"] = parseFloat(val);
									}
									setValidationErrors({});
								},
								onBlur: (e) => {
									let val = e.target.value;
									if (val === "" || isNaN(val)) {
										row._valuesCache["tankFuelTypes.uncertainityValue"] = 0;
									}
								},
							};
						},
					},
				],
			},
			{
				accessorKey: "fuelInTanksDensity",
				header: "Method to determine actual density values of fuel in tanks ",
				editVariant: "select",
				editSelectOptions: fuelBunkeredDensities,
				Cell: ({ cell }) => labelByfuelBunkered[cell.getValue()] ?? cell.getValue(),

				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
						verticalAlign: "middle",
					},
					SelectProps: {
						MenuProps: {
							disableScrollLock: true,
							textAlign: "center",
							PaperProps: {
								sx: {
									maxHeight: 250,
									boxShadow: 3,
									maxWidth: 180,
									overflowY: "auto",
									"&::-webkit-scrollbar": {
										width: "8px",
									},
									"&::-webkit-scrollbar-thumb": {
										backgroundColor: "darkgray",
										borderRadius: "3px",
									},
									"&::-webkit-scrollbar-track": {
										backgroundColor: "#f0f0f0",
									},
								},
							},
							anchorOrigin: {
								vertical: "bottom",
								horizontal: "left",
							},
							transformOrigin: {
								vertical: "top",
								horizontal: "left",
							},
							getContentAnchorEl: null,
						},
					},
				},
			},
		],
		[fuelTypeOptions, validationErrors]
	);

	const handleDeleteBtnClick = (rowIndex) => {
		setConfirmDelete(true);
		setDeletingRowIndex(rowIndex);
	};

	const table = useMaterialReactTable({
		columns,
		data: fuelData || [],
		enableStickyHeader: true,
		enableSorting: false,
		enableRowSelection: false,
		enableColumnPinning: false,
		enableTopToolbar: true,
		enableBottomToolbar: false,
		enableColumnActions: false,
		getNestedRowValues: true,
		createDisplayMode: "row",
		editDisplayMode: "row",
		enableEditing: true,
		enablePagination: false,
		muiTableHeadCellProps: {
			sx: {
				color: "rgb(255 255 255)",
				backgroundColor: "#042a4c",
				position: "sticky",
				borderRight: "1px solid lightgray",
				"&:last-child": {
					borderRight: "none",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
		muiTableContainerProps: {
			sx: {
				maxHeight: "520px",
			},
		},
		displayColumnDefOptions: {
			"mrt-row-actions": {
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
					},
				},
			},
		},
		muiTableBodyCellProps: ({ row, cell }) => {
			return {
				sx: {
					borderRight: "1px solid lightgray",
					"&:last-child": {
						borderRight: "none",
					},
					color: "",
				},
			};
		},
		muiTableBodyRowProps: ({ row }) => ({
			sx: {
				"&:not(:last-of-type)": {
					borderBottom: "1px solid lightgray",
				},
				verticalAlign: "top",
			},
		}),
		muiTableHeadRowProps: {
			sx: {
				borderBottom: "2px solid lightgray",
			},
		},
		columnOrder: ["fuel_type_tank", "bunkeredFuelDensity", "fuelInTanksDensity"],

		onCreatingRowSave: handleCreateFuel,
		onEditingRowCancel: resetActiveRow,
		onCreatingRowCancel: resetActiveRow,
		onEditingRowSave: handleUpdateEmissions,
		renderToolbarInternalActions: ({ table }) => <></>,
		renderRowActions: ({ row, table }) => {
			return (
				<Box sx={{ display: "flex", gap: "1rem" }}>
					<Tooltip title="Edit">
						<IconButton style={{ padding: "0px" }} onClick={() => handleFuelActions(table, row, RowActions.Edit)}>
							<EditIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title="Delete">
						<IconButton style={{ padding: "0px" }} color="error" onClick={() => handleDeleteBtnClick(row?.index)}>
							<DeleteIcon />
						</IconButton>
					</Tooltip>
				</Box>
			);
		},
		renderTopToolbarCustomActions: ({ table }) => (
			<div className="mt-1">
				<Button
					startIcon={<Icon icon={ICONS.ADD} />}
					onClick={() => {
						handleFuelActions(table, null, RowActions.Add);
					}}
					disabled={!!table.getState().creatingRow}
				>
					Add Fuel Tank
				</Button>
			</div>
		),
	});

	return (
		<>
			<div className="fuel-registry-c26 mt-1">
				<MaterialReactTable table={table} />
			</div>
			<div className="confirmation-modal-container">
				<Modal
					isOpen={confirmDelete}
					modalHeader
					padding={"70px"}
					headerHeight={"50px"}
					showFooter
					onCloseClickListener={() => {
						setConfirmDelete(false);
					}}
					footer={
						<div className="d-flex justify-content-end fixed-action-btns p-4">
							<Button
								className="modal-delete-btn"
								onClick={() => {
									handleDeleteEmissions(deletingRowIndex);
								}}
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
							>
								Delete
							</Button>
							<Button
								className="modal-cancel-btn"
								onClick={() => {
									setConfirmDelete(false);
								}}
							>
								Cancel
							</Button>
						</div>
					}
				>
					<div>Are you sure you want to delete this fuel tank?</div>
				</Modal>
			</div>
		</>
	);
});

export default FeuC14DeterminationOfDensity;
